import { useEffect } from 'react'

// Back/forward-cache restore hook. A page that navigates AWAY with a "busy" flag set — a sign-in leaving
// for Google, "Entrar" bouncing to the Accounts App, a checkout leaving for the provider — is restored by
// Safari and Chrome from bfcache with its React state INTACT when the person presses Back. The flag is
// still true, the spinner is still up, and nothing ever clears it: an Abandoned Sign-in that wedges the
// login page. `pageshow` with `persisted` fires ONLY on that restore (never on a fresh load), so the
// departure surface can reset itself exactly then and nowhere else.

/** Subscribe to a bfcache restore of this page. Returns the unsubscribe. */
export function onPageRestore(reset: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const onShow = (e: PageTransitionEvent) => {
    if (e.persisted) reset()
  }
  window.addEventListener('pageshow', onShow)
  return () => window.removeEventListener('pageshow', onShow)
}

/**
 * Run `reset` when this page comes back from the back/forward cache. Use it on any surface that sets a
 * blocking "leaving…" state right before a full-page navigation, so Back does not strand the person on a
 * spinner that no code will ever clear.
 */
export function useResetOnPageRestore(reset: () => void): void {
  useEffect(() => onPageRestore(reset), [reset])
}
